import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema } from './common.js';
import { DecisionRequestSchema, WorkflowDefinitionSchema, WorkItemKindSchema } from './workflow.js';
export const RepositoryKeySchema = z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z][a-z0-9-]*$/);
export const ProjectRepositorySchema = z
    .object({
    key: RepositoryKeySchema,
    repositoryUrl: z.string().url(),
    repositoryOwner: z.string().min(1).max(100),
    repositoryName: z.string().min(1).max(100),
    defaultBranch: z.string().min(1).max(200),
    testCommand: z.string().min(1).max(2_000),
})
    .strict();
export const ProjectRepositoriesSchema = z
    .array(ProjectRepositorySchema)
    .min(1)
    .max(50)
    .refine((repositories) => new Set(repositories.map(({ key }) => key)).size === repositories.length, {
    message: 'repository keys must be unique',
});
export const RepositorySelectionFileSchema = z
    .array(RepositoryKeySchema)
    .max(50)
    .refine((keys) => new Set(keys).size === keys.length, {
    message: 'repository keys must be unique',
});
export const TestDeployModeSchema = z.enum(['WEBHOOK', 'PUSH_TRIGGERED', 'FLOW']);
export const FlowPipelineIdSchema = z
    .string()
    .min(1)
    .max(32)
    .regex(/^[1-9]\d*$/);
export const ProjectSchema = z
    .object({
    id: EntityIdSchema,
    key: z
        .string()
        .min(2)
        .max(16)
        .regex(/^[A-Z][A-Z0-9]*$/),
    name: z.string().min(1).max(100),
    repositories: ProjectRepositoriesSchema,
    testDeployMode: TestDeployModeSchema.default('WEBHOOK'),
    testDeployWebhookUrl: z.string().url().nullable(),
    testDeployFlowPipelineId: FlowPipelineIdSchema.nullable().default(null),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict();
export const CreateProjectInputSchema = z
    .object({
    key: ProjectSchema.shape.key,
    name: ProjectSchema.shape.name,
    repositories: ProjectRepositoriesSchema.optional(),
    repositoryUrl: ProjectRepositorySchema.shape.repositoryUrl.optional(),
    repositoryOwner: ProjectRepositorySchema.shape.repositoryOwner.optional(),
    repositoryName: ProjectRepositorySchema.shape.repositoryName.optional(),
    defaultBranch: ProjectRepositorySchema.shape.defaultBranch.optional(),
    testCommand: ProjectRepositorySchema.shape.testCommand.optional(),
    testDeployMode: TestDeployModeSchema.default('WEBHOOK'),
    testDeployWebhookUrl: ProjectSchema.shape.testDeployWebhookUrl,
    testDeployFlowPipelineId: ProjectSchema.shape.testDeployFlowPipelineId,
})
    .strict()
    .refine((input) => !(input.repositories &&
    (input.repositoryUrl ||
        input.repositoryOwner ||
        input.repositoryName ||
        input.defaultBranch ||
        input.testCommand)), { message: 'repositories cannot be combined with legacy repository fields' })
    .refine((input) => Boolean(input.repositories) ||
    Boolean(input.repositoryUrl &&
        input.repositoryOwner &&
        input.repositoryName &&
        input.defaultBranch &&
        input.testCommand), { message: 'repositories or a complete legacy repository configuration is required' })
    .refine((input) => input.testDeployMode !== 'FLOW' || Boolean(input.testDeployFlowPipelineId), {
    message: 'testDeployFlowPipelineId is required for Flow deployment',
    path: ['testDeployFlowPipelineId'],
});
export const WorkItemStatusSchema = z.enum([
    'QUEUED',
    'RUNNING',
    'WAITING_DECISION',
    'NEEDS_ATTENTION',
    'DELIVERED',
    'FAILED',
    'CANCELLED',
    'INTERRUPTED',
]);
export const WorkItemSourceSchema = z.enum(['FEISHU', 'WEB', 'API']);
export const WorkItemTriggerSchema = z
    .object({
    openId: z.string().min(1).max(200),
    name: z.string().trim().min(1).max(200).nullable(),
})
    .strict();
export const WorkItemSchema = z
    .object({
    id: EntityIdSchema,
    key: z.string().regex(/^[A-Z][A-Z0-9]*-\d+$/),
    projectId: EntityIdSchema,
    kind: WorkItemKindSchema,
    title: z.string().min(1).max(200),
    description: z.string().min(1).max(20_000),
    status: WorkItemStatusSchema,
    source: WorkItemSourceSchema,
    sourceRef: z.string().max(500).nullable(),
    sourceGroupRef: z.string().max(500).nullable().optional(),
    requestedBy: z.string().min(1).max(200),
    triggeredBy: WorkItemTriggerSchema.nullable(),
    branchName: z.string().min(1).max(240),
    sandboxId: z.string().max(200).nullable(),
    currentWorkflowRunId: EntityIdSchema.nullable(),
    revision: z.number().int().min(0),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict();
export const CreateWorkItemInputSchema = z
    .object({
    projectId: EntityIdSchema,
    kind: WorkItemKindSchema,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(20_000),
    source: WorkItemSourceSchema.default('API'),
    sourceRef: z.string().max(500).nullable().default(null),
    sourceGroupRef: z.string().max(500).nullable().default(null),
    requestedBy: z.string().min(1).max(200),
    triggeredBy: WorkItemTriggerSchema.nullable().default(null),
    workflowKey: z.string().min(1).max(64).optional(),
})
    .strict();
export const WorkflowRunStatusSchema = z.enum([
    'QUEUED',
    'RUNNING',
    'WAITING_DECISION',
    'NEEDS_ATTENTION',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
]);
export const WorkflowDecisionPolicySchema = z.enum(['ASK', 'USE_RECOMMENDED']);
export const StageRunStatusSchema = z.enum([
    'QUEUED',
    'RUNNING',
    'COMPLETED',
    'FAILED',
    'INTERRUPTED',
]);
export const WorkflowDecisionStatusSchema = z.enum(['PENDING', 'RESOLVED', 'CANCELLED']);
export const WorkflowDecisionSchema = z
    .object({
    id: EntityIdSchema,
    workItemId: EntityIdSchema,
    workflowRunId: EntityIdSchema,
    stageRunId: EntityIdSchema,
    stageKey: z.string().min(1).max(64),
    status: WorkflowDecisionStatusSchema,
    onDecision: z.string().min(1).max(64),
    questionIndex: z.number().int().min(1),
    questionTotal: z.number().int().min(1),
    request: DecisionRequestSchema,
    selectedOptionKey: z.string().min(1).max(64).nullable(),
    decidedBy: z.string().min(1).max(200).nullable(),
    decidedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
})
    .strict();
export const ArtifactKindSchema = z.enum([
    'ANALYSIS',
    'OUTCOME',
    'PLAN',
    'DOCUMENT',
    'FINDINGS',
    'TEST_REPORT',
    'DIFF',
    'COMMIT',
    'BRANCH',
    'PULL_REQUEST',
    'TEST_DEPLOYMENT',
    'LOG',
]);
export const WorkflowRunSchema = z
    .object({
    id: EntityIdSchema,
    workItemId: EntityIdSchema,
    workflowKey: z.string().min(1).max(64),
    workflowVersion: z.string().min(1).max(32),
    definition: WorkflowDefinitionSchema,
    status: WorkflowRunStatusSchema,
    decisionPolicy: WorkflowDecisionPolicySchema.default('ASK'),
    decisionPolicyChangedBy: z.string().min(1).max(200).nullable().default(null),
    decisionPolicyChangedAt: IsoDateTimeSchema.nullable().default(null),
    currentStageKey: z.string().min(1).max(64).nullable(),
    currentAttempt: z.number().int().min(1),
    nextAttemptAt: IsoDateTimeSchema.nullable().default(null),
    transitionCount: z.number().int().min(0),
    revision: z.number().int().min(0),
    leaseOwner: z.string().max(200).nullable(),
    leaseExpiresAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .strict();
export const StageRunSchema = z
    .object({
    id: EntityIdSchema,
    workflowRunId: EntityIdSchema,
    stageKey: z.string().min(1).max(64),
    attempt: z.number().int().min(1),
    transitionIndex: z.number().int().min(0),
    status: StageRunStatusSchema,
    exitCode: z.number().int().nullable(),
    errorCode: z.string().max(100).nullable(),
    errorMessage: z.string().max(2_000).nullable(),
    startedAt: IsoDateTimeSchema,
    completedAt: IsoDateTimeSchema.nullable(),
})
    .strict();
export const WorkItemFailureKindSchema = z.enum([
    'VERIFICATION',
    'EXECUTION',
    'CONTRACT',
    'DELIVERY',
]);
export const WorkItemFailureRecoverySchema = z.discriminatedUnion('available', [
    z
        .object({
        available: z.literal(true),
        action: z.literal('FIX_AND_RESUME'),
        targetStageKey: z.string().min(1).max(64),
    })
        .strict(),
    z.object({ available: z.literal(false) }).strict(),
]);
export const WorkItemFailureSchema = z
    .object({
    workflowRunId: EntityIdSchema,
    stageRunId: EntityIdSchema,
    stageKey: z.string().min(1).max(64),
    kind: WorkItemFailureKindSchema,
    code: z.string().min(1).max(100),
    summary: z.string().min(1).max(500),
    repositoryKey: RepositoryKeySchema.nullable(),
    command: z.string().min(1).max(4_096).nullable(),
    exitCode: z.number().int().nullable(),
    excerpt: z.string().min(1).max(2_000).nullable(),
    recovery: WorkItemFailureRecoverySchema,
})
    .strict();
const RepositoryCommitShaSchema = z
    .string()
    .regex(/^[0-9a-f]{40}(?:[0-9a-f]{24})?$/u, 'must be a git commit SHA');
export const VerificationBaselineSchema = z
    .object({
    schemaVersion: z.literal(1),
    repositories: z
        .record(RepositoryKeySchema, z
        .object({
        baseSha: RepositoryCommitShaSchema,
        testCommand: z.string().min(1).max(2_000),
        verificationCommand: z.string().min(1).max(4_096).optional(),
    })
        .strict())
        .refine((repositories) => Object.keys(repositories).length > 0, {
        message: 'at least one baseline repository is required',
    }),
})
    .strict();
export const VerificationClassificationSchema = z.enum([
    'PASSED',
    'BASELINE_ONLY',
    'CURRENT_CHANGE',
    'MIXED',
    'UNCLASSIFIED',
]);
export const VerificationResultSchema = z
    .object({
    schemaVersion: z.literal(1),
    classification: VerificationClassificationSchema,
    repositoryKey: RepositoryKeySchema.nullable(),
    baseSha: RepositoryCommitShaSchema.nullable(),
    testCommand: z.string().min(1).max(2_000).nullable(),
    baselineFailures: z.array(z.string().min(1).max(500)).max(200),
    currentFailures: z.array(z.string().min(1).max(500)).max(200),
    newFailures: z.array(z.string().min(1).max(500)).max(200),
})
    .strict();
export const ArtifactSchema = z
    .object({
    id: EntityIdSchema,
    workItemId: EntityIdSchema,
    workflowRunId: EntityIdSchema,
    stageRunId: EntityIdSchema.nullable(),
    kind: ArtifactKindSchema,
    name: z.string().min(1).max(200),
    path: z.string().max(1_000).nullable(),
    url: z.string().url().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    createdAt: IsoDateTimeSchema,
})
    .strict();
export function projectRepositories(project) {
    return project.repositories;
}
//# sourceMappingURL=domain.js.map